import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Combined List Table Component
 * Manages a table of related products with quantity selectors and multi add-to-cart
 */
class CombinedListTableComponent extends Component {
  /** @type {Map<string, number>} */
  selectedQuantities;
  
  /** @type {{ texts?: { [key: string]: string } }} */
  settings;
  
  /** @type {Map<string, number>} */
  updateTimeouts;
  
  loadSettings() {
    const settingsScript = this.querySelector('[data-combined-list-settings]');
    if (settingsScript) {
      try {
        this.settings = JSON.parse(settingsScript.textContent);
        console.log('✅ Combined list settings loaded:', this.settings);
      } catch (error) {
        console.error('❌ Error loading settings:', error);
        this.settings = { texts: {} };
      }
    } else {
      console.warn('⚠️ Settings script not found');
      this.settings = { texts: {} };
    }
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Load settings from JSON
    this.loadSettings();
    
    // Get references to DOM elements
    this.tableBody = this.querySelector('[data-table-body]');
    this.summary = this.querySelector('[data-selection-summary]');
    this.multiAddToCartButton = this.querySelector('[data-multi-add-to-cart]');
    this.addToCartError = this.querySelector('[data-add-to-cart-error]');
    
    // Initialize state
    this.selectedQuantities = new Map(); // Map of variantId -> quantity
    this.updateTimeouts = new Map(); // Map to prevent duplicate updates
    
    console.log('🚀 Combined List Table initialized:', {
      tableBody: !!this.tableBody,
      summary: !!this.summary,
      rows: this.querySelectorAll('.combined-table__row').length
    });
    
    // Attach event listeners
    this.attachQuantityListeners();
    this.attachSingleAddListeners();
    
    // Initialize button states (disable buttons with quantity 0)
    this.initializeButtonStates();
    
    // Add event listener for multi add to cart button
    if (this.multiAddToCartButton) {
      this.multiAddToCartButton.addEventListener('click', this.handleMultiAddToCart.bind(this));
    }
  }

  /**
   * Initialize all add button states on page load
   */
  initializeButtonStates() {
    const quantityInputs = this.querySelectorAll('input[data-variant-id]');
    quantityInputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        this.updateAddButtonState(input);
      }
    });
  }

  /**
   * Update quantity with debounce to prevent duplicate updates
   * @param {string} variantId 
   * @param {number} quantity 
   * @param {string} source 
   */
  updateQuantityDebounced(variantId, quantity, source) {
    // Clear any existing timeout for this variant
    if (this.updateTimeouts.has(variantId)) {
      clearTimeout(this.updateTimeouts.get(variantId));
    }
    
    // Set a new timeout
    const timeoutId = setTimeout(() => {
      console.log(`✅ Quantity updated via ${source}:`, { variantId, quantity });
      
      // Update selected quantities map
      if (quantity > 0) {
        this.selectedQuantities.set(variantId, quantity);
      } else {
        this.selectedQuantities.delete(variantId);
      }
      
      // Update summary and button
      this.updateSummary();
      this.updateMultiAddButton();
      
      // Clean up timeout
      this.updateTimeouts.delete(variantId);
    }, 10); // 10ms debounce
    
    this.updateTimeouts.set(variantId, timeoutId);
  }

  attachQuantityListeners() {
    // ✅ NEW APPROACH: Bypass native component, handle clicks directly
    this.addEventListener('click', (event) => {
      const target = event.target;
      
      if (!(target instanceof HTMLElement)) return;
      
      // Handle plus button
      if (target.matches('.quantity-selector__button--plus') || target.closest('.quantity-selector__button--plus')) {
        event.preventDefault();
        event.stopPropagation();
        
        const button = target.matches('.quantity-selector__button--plus') ? target : target.closest('.quantity-selector__button--plus');
        const quantitySelector = button?.closest('.quantity-selector');
        const input = quantitySelector?.querySelector('input[data-variant-id]');
        
        if (input instanceof HTMLInputElement && input.dataset.variantId) {
          const currentValue = parseInt(input.value) || 0;
          const max = parseInt(input.max) || 999;
          const newValue = Math.min(currentValue + 1, max);
          
          input.value = String(newValue);
          
          console.log('➕ Plus button clicked:', { variantId: input.dataset.variantId, newValue });
          
          // Update our state and button state
          this.updateQuantityDebounced(input.dataset.variantId, newValue, 'plus button');
          this.updateAddButtonState(input);
        }
      }
      
      // Handle minus button
      if (target.matches('.quantity-selector__button--minus') || target.closest('.quantity-selector__button--minus')) {
        event.preventDefault();
        event.stopPropagation();
        
        const button = target.matches('.quantity-selector__button--minus') ? target : target.closest('.quantity-selector__button--minus');
        const quantitySelector = button?.closest('.quantity-selector');
        const input = quantitySelector?.querySelector('input[data-variant-id]');
        
        if (input instanceof HTMLInputElement && input.dataset.variantId) {
          const currentValue = parseInt(input.value) || 0;
          const min = parseInt(input.min) || 0;
          const newValue = Math.max(currentValue - 1, min);
          
          input.value = String(newValue);
          
          console.log('➖ Minus button clicked:', { variantId: input.dataset.variantId, newValue });
          
          // Update our state and button state
          this.updateQuantityDebounced(input.dataset.variantId, newValue, 'minus button');
          this.updateAddButtonState(input);
        }
      }
    });
    
    // Listen to direct input changes (manual typing)
    this.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.dataset.variantId) {
        const variantId = event.target.dataset.variantId;
        const quantity = parseInt(event.target.value) || 0;
        
        console.log('⌨️ Direct input change:', { variantId, quantity });
        
        // Use debounced update
        this.updateQuantityDebounced(variantId, quantity, 'direct input');
        this.updateAddButtonState(event.target);
      }
    });
    
    console.log('✅ Quantity listeners attached (custom implementation, bypassing native component)');
  }

  /**
   * Update the add button state based on quantity
   * @param {HTMLInputElement} input 
   */
  updateAddButtonState(input) {
    const row = input.closest('.combined-table__row');
    if (!row) return;
    
    const addButton = row.querySelector('[data-add-single]');
    if (!addButton || !(addButton instanceof HTMLButtonElement)) return;
    
    const quantity = parseInt(input.value) || 0;
    
    if (quantity === 0) {
      addButton.disabled = true;
      addButton.classList.add('button--disabled');
    } else {
      addButton.disabled = false;
      addButton.classList.remove('button--disabled');
    }
  }

  attachSingleAddListeners() {
    // Listen to single add to cart buttons
    const singleAddButtons = this.querySelectorAll('[data-add-single]');
    singleAddButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        this.handleSingleAddToCart(event.currentTarget);
      });
    });
  }

  async handleSingleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const row = button.closest('.combined-table__row');
    const quantityInput = row.querySelector('input[data-variant-id]');
    const quantity = parseInt(quantityInput?.value) || 0;
    
    // Prevent adding to cart if quantity is 0
    if (quantity === 0) {
      console.warn('⚠️ Cannot add to cart: quantity is 0');
      return;
    }
    
    console.log('🛒 Single add to cart:', { variantId, quantity });
    
    // Disable button and show loading state
    button.disabled = true;
    const originalContent = button.innerHTML;
    button.classList.add('loading');
    button.innerHTML = '<span class="button-spinner"></span>';
    
    try {
      await this.addToCart([{ id: variantId, quantity }]);
      
      // Notify Planet's cart drawer via synthetic form submit
      this.notifyPlanet();

      // Success feedback
      button.classList.remove('loading');
      button.classList.add('success');
      button.innerHTML = `
        <svg class="checkmark" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>AÑADIDO</span>
      `;

      // Highlight row
      row.classList.add('row-added');

      // Fly-to-cart animation (reuses native fly-to-cart custom element)
      const imageSrc = row.dataset.productImage || null;
      this.animateFlyToCart(button, imageSrc);
      
      // Reset after delay
      setTimeout(() => {
        button.classList.remove('success');
        button.innerHTML = originalContent;
        button.disabled = false;
        row.classList.remove('row-added');
      }, 2500);
      
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      button.classList.remove('loading');
      button.classList.add('error');
      button.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span>ERROR</span>
      `;
      
      setTimeout(() => {
        button.classList.remove('error');
        button.innerHTML = originalContent;
        button.disabled = false;
      }, 2000);
      
      this.showError(error.message || this.settings.texts?.cartError || 'Error al añadir al carrito');
    }
  }
  
  /**
   * Open Planet's smart-cart drawer after a successful cart add.
   * Uses Planet's programmatic API: PlanetDataset.SmartCart.methods.openDrawer(true)
   * The `true` argument triggers a cart refetch before opening so the drawer shows updated items.
   */
  notifyPlanet() {
    window.PlanetDataset?.SmartCart?.methods?.openDrawer?.(true);
  }

  /**
   * Trigger the native fly-to-cart bezier animation
   * Reuses the <fly-to-cart> custom element defined in product-form.js
   * @param {HTMLElement} sourceElement - The element to animate from
   * @param {string|null} imageSrc - Product image URL for the animation
   */
  animateFlyToCart(sourceElement, imageSrc) {
    // Respect the global add-to-cart animation setting
    if (this.dataset.addToCartAnimation !== 'true') return;
    if (!imageSrc) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon) return;

    // Check that the fly-to-cart custom element is registered
    if (!customElements.get('fly-to-cart')) return;

    const flyEl = /** @type {any} */ (document.createElement('fly-to-cart'));
    flyEl.style.setProperty('background-image', `url(${imageSrc})`);
    flyEl.source = sourceElement;
    flyEl.destination = cartIcon;
    document.body.appendChild(flyEl);
  }

  async handleMultiAddToCart() {
    console.log('🛒 Multi add to cart triggered');
    console.log('📦 Selected quantities:', Array.from(this.selectedQuantities.entries()));
    
    if (this.selectedQuantities.size === 0) {
      console.warn('⚠️ No products selected');
      return;
    }
    
    // Prepare items for cart
    const items = Array.from(this.selectedQuantities.entries()).map(([variantId, quantity]) => ({
      id: variantId,
      quantity: quantity
    }));
    
    console.log('📦 Items to add:', items);
    
    // Disable button and show loading state
    this.multiAddToCartButton.disabled = true;
    const buttonText = this.multiAddToCartButton.querySelector('.button-text');
    const originalText = buttonText?.textContent || '';
    if (buttonText) {
      buttonText.textContent = this.settings.texts?.adding || 'Añadiendo...';
    }
    
    try {
      await this.addToCart(items);
      
      // Notify Planet's cart drawer via synthetic form submit
      this.notifyPlanet();

      // Show success state
      if (buttonText) {
        buttonText.textContent = this.settings.texts?.added || '¡Añadido!';
      }

      // Fly-to-cart animation using the first product's image
      const firstVariantId = items[0]?.id;
      if (firstVariantId) {
        const firstInput = this.querySelector(`input[data-variant-id="${firstVariantId}"]`);
        const firstRow = firstInput?.closest('.combined-table__row');
        const imageSrc = firstRow?.dataset?.productImage || null;
        this.animateFlyToCart(this.multiAddToCartButton, imageSrc);
      }
      
      // Keep quantities as reference (don't clear)
      // this.clearAllQuantities();
      
      // Reset button after delay
      setTimeout(() => {
        if (buttonText) {
          buttonText.textContent = this.settings.texts?.addToCart || 'Añadir al carrito';
        }
        this.multiAddToCartButton.disabled = false;
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      if (buttonText) {
        buttonText.textContent = originalText;
      }
      this.multiAddToCartButton.disabled = false;
      this.showError(error.message || this.settings.texts?.cartError || 'Error al añadir al carrito');
    }
  }

  async addToCart(items) {
    console.log('🛒 Adding items to cart:', items);
    
    // Get cart sections that need to be updated
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    // Prepare form data for multiple items
    const formData = new FormData();
    
    // Add each item
    items.forEach((/** @type {{ id: string, quantity: number }} */ item, /** @type {number} */ index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity.toString());
    });
    
    // Add sections to update
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });

    const data = await response.json();
    console.log('✅ Cart response:', data);
    
    // Check if response has error status
    if (data.status) {
      throw new Error(data.description || data.message || 'Error al añadir al carrito');
    }
    
    // Dispatch cart add event — itemCount must be in `data` for cart-icon.js to update the bubble
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    this.dispatchEvent(new CartAddEvent({}, '', {
      source: 'combined-list-table',
      itemCount: totalQuantity,
    }));
    
    return data;
  }

  clearAllQuantities() {
    console.log('🧹 Clearing all quantities');
    
    // Clear the map
    this.selectedQuantities.clear();
    
    // Keep quantities as reference (don't reset inputs)
    // const quantityInputs = this.querySelectorAll('input[data-variant-id]');
    // quantityInputs.forEach(input => {
    //   input.value = 0;
    // });
    
    // Update summary and button
    this.updateSummary();
    this.updateMultiAddButton();
  }

  updateSummary() {
    if (!this.summary) return;
    
    const countElement = this.summary.querySelector('[data-summary-count]');
    const totalElement = this.summary.querySelector('[data-summary-total]');
    
    let totalPrice = 0;
    let totalCount = 0;
    
    // Calculate totals from selected quantities
    this.selectedQuantities.forEach((quantity, variantId) => {
      const input = this.querySelector(`input[data-variant-id="${variantId}"]`);
      if (input) {
        const price = parseFloat(input.dataset.price) || 0;
        totalPrice += price * quantity;
        totalCount += quantity;
      }
    });
    
    console.log('💰 Summary updated:', { totalCount, totalPrice });
    
    // Update DOM
    if (countElement) {
      countElement.textContent = totalCount;
    }
    
    if (totalElement) {
      const formattedPrice = this.formatMoney(totalPrice);
      totalElement.textContent = formattedPrice;
    }
  }

  updateMultiAddButton() {
    if (!this.multiAddToCartButton) return;
    
    const count = this.selectedQuantities.size;
    const totalQuantity = Array.from(this.selectedQuantities.values()).reduce((sum, qty) => sum + qty, 0);
    const buttonText = this.multiAddToCartButton.querySelector('.button-text');
    
    if (!buttonText) return;
    
    if (count === 0) {
      this.multiAddToCartButton.disabled = true;
      buttonText.textContent = this.settings.texts?.selectProducts || 'Selecciona productos';
    } else {
      this.multiAddToCartButton.disabled = false;
      
      // Format button text with placeholders
      let text = this.settings.texts?.addToCart || 'Añadir {count} producto{plural} al carrito';
      text = text.replace('{count}', String(totalQuantity));
      text = text.replace('{plural}', totalQuantity > 1 ? 's' : '');
      
      buttonText.textContent = text;
    }
    
    console.log('🔘 Button updated:', { count, totalQuantity, disabled: this.multiAddToCartButton.disabled });
  }

  showError(message) {
    if (!this.addToCartError) return;
    
    const errorMessage = this.addToCartError.querySelector('.error-message');
    if (errorMessage) {
      errorMessage.textContent = message;
    }
    
    this.addToCartError.classList.remove('hidden');
    
    // Hide error after 5 seconds
    setTimeout(() => {
      this.addToCartError.classList.add('hidden');
    }, 5000);
  }

  formatMoney(cents) {
    // Simple money formatting - adjust based on your theme's money format
    const amount = cents / 100;
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }
}

customElements.define('combined-list-table-component', CombinedListTableComponent);

export default CombinedListTableComponent;
